import { useScopeBusy } from '@/modules/shared/hooks/useScopeBusy'
import { taskScopeKey } from '@/modules/shared/utils/taskScopeKey'
import * as React from 'react'
import {
  compare,
  getNettoMismatchForDisplay,
  isAmountEdited,
  isDomesticTripIncluded,
  isRowEdited,
  pickReward,
  removeKey,
  toNumberOrUndefined,
} from './utils'
import { useGroupedPayroll } from './hooks/useGroupedPayroll'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { useDrivers } from '@/modules/shared/context/drivers/DriversContext'
import { PayrollErrorScreen } from './screen/PayrollErrorScreen'
import { PayrollEmptyScreen } from './screen/PayrollEmptyScreen'
import { Loader } from '@/ui'
import { PayrollResultsTableWrapper } from './components/PayrollResultsTableWrapper'
import { PayrollResultsTableRow } from './components/PayrollResultsTableRow'
import { PayrollDriverRow } from '@/lib/api/company/timesheet/groupedTimesheet/types'
import { CellKey } from './types'
import { useBonusRewardsToolbar } from './hooks/useBonusRewardsToolbar'
import { useSaveAndCalculate } from './hooks/useSaveAndCalculate'
import { useForeignTripConfirm } from '../useForeignTripConfirm'
import { useOutOfDateRateConfirm } from '../useOutOfDateRateConfirm'
import { useQuarterExchangeRateStatusQuery } from '@/lib/api/company/timesheet/quarterExchangeRateStatus/useQuarterExchangeRateStatusQuery'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { PrintActionContainer } from '@/modules/printouts/containers/PrintActionContainer'
import { TimesheetPrintShortcuts } from '@/modules/printouts/components/TimesheetPrintShortcuts'
import { formatDate } from '@/modules/shared/utils/date'
import { ReportType } from '@/app/constants/reportType'
import { useUploadTimesheetFileMutation } from '@/lib/api/company/timesheet/fileUpload/useUploadTimesheetFileMutation'
import { toast } from '@/lib/toasts/api'
import { useRemoveTimesheets } from '@/modules/drivers/hooks/useRemoveTimesheets'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useDirtyStore } from '@/modules/shared/stores/dirtyStore'
import { useShallow } from 'zustand/shallow'
import PayrollActionBar from '@/modules/drivers/shared/actionBars/PayrollActionBar'
import { PayrollActionBarProvider } from './PayrollActionBarContext'
import { useSideSelectionStore } from '@/modules/drivers/stores/sideSelectionStore'
import { fetchExpectedNettoFromEasyTms } from '@/lib/api/company/timesheet/groupedTimesheet/fetchExpectedNettoFromEasyTms'
import { fetchMandatesAdvancesFromEasyTms } from '@/lib/api/company/timesheet/groupedTimesheet/fetchMandatesAdvancesFromEasyTms'

const GROUP_PAYROLL_AVAILABLE_REPORTS = [
  ReportType.TIMESHEET_FOREIGN_AMOUNTS_REPORT,
  ReportType.TIMESHEET_LUMPSUM_SETTLEMENT,
  ReportType.TIMESHEET_LUMPSUM_UTILIZATION,
  ReportType.TIMESHEET_APPLICATION_FOR_LEAVE_FOR_DRIVERS,
  ReportType.TIMESHEET,
  ReportType.TIMESHEET_LEAVES_CALCULATION,
  ReportType.TIMESHEET_LEAVE_EQUIVALENT_CALCULATION,
  ReportType.REWARDS_TO_ACCEPT_REPORT,
  ReportType.MOBILITY_PACKAGE_PAYROL_REPORT,
  ReportType.TRIP_COSTS,
  ReportType.TIMESHEET_AMOUNTS_REPORT,
  ReportType.TIMESHEET_PAYMENT_DETAILS,
  ReportType.TIMESHEET_PAYMENT_DETAILS_CSV,
  ReportType.TIMESHEET_DETAILED_WAGE_REPORT,
  ReportType.TIMESHEET_YEAR_SUMMARY,
  ReportType.TIMESHEET_FOREIGN_AMOUNT_SUMMARY,
  ReportType.TIMESHEET_LUMPSUM_SETTLEMENT_SUMMARY,
  ReportType.TIMESHEET_WORK_COSTS,
  ReportType.TIMESHEET_PAYMENT_REPORT,
  ReportType.DRIVER_WORKING_DAYS,
  ReportType.TIMESHEET_LEAVE_DAYS_REPORT,
]

interface Props {
  companyId: string
  hideActionBar?: boolean

  hideExtraColumns?: boolean
  submitRef?: React.MutableRefObject<(() => void) | null>
  onSubmitSuccess?: () => void
  onSubmitError?: (err: unknown) => void
}

type DriverField =
  | 'offPay'
  | 'sickPay'
  | 'benefits'
  | 'bonus'
  | 'nettoAmount'
  | 'reward'
  | 'includeBusinessTrip'
  | 'isSelected'
  | 'leaveAddition'
  | 'finalLeaveHours'

export const GroupPayrollView = ({
  companyId,
  hideActionBar = false,
  hideExtraColumns = false,
  submitRef,
  onSubmitSuccess,
  onSubmitError,
}: Props) => {
  const { t } = useLocalize()
  // driversById {driverID -> driver}
  const [includeBusinessTripForDriversId, setIncludeBusinessTripForDriversId] =
    React.useState<Record<string, boolean>>({})
  const [sickPayByDriverId, setSickPayByDriverId] = React.useState<
    Record<string, number>
  >({})
  const [benefitsByDriverId, setBenefitsByDriverId] = React.useState<
    Record<string, number>
  >({})
  const [bonusesByDriverId, setBonusesByDriverId] = React.useState<
    Record<string, number>
  >({})
  const [offPayByDriverId, setOffPayByDriverId] = React.useState<
    Record<string, number>
  >({})
  const [finalLeaveHoursByDriverId, setFinalLeaveHoursByDriverId] =
    React.useState<Record<string, number>>({})
  const [seletedRowsIds, setSeletedRowsIds] = React.useState<
    Record<string, boolean>
  >({})
  const [expectedNettoAmountByDriverId, setExpectedNettoAmountByDriverId] =
    React.useState<Record<string, number>>({})

  const [
    expectedNettoBeforeTripsByDriverId,
    setExpectedNettoBeforeTripsByDriverId,
  ] = React.useState<Record<string, number>>({})
  const [rewardByDriverId, setRewardByDriverId] = React.useState<
    Record<string, number>
  >({})
  const [tripCostsByDriverId, setTripCostsByDriverId] = React.useState<
    Record<string, number>
  >({})
  const [leaveAdditionByDriverId, setLeaveAdditionByDriverId] = React.useState<
    Record<string, Record<number, number>>
  >({})
  // Fines (mandat) and advances (zaliczka) from EasyTMS/BFM. Grid columns are display-only;
  // populated on grid load and on "Przelicz" (not persisted server-side — load-time fetch keeps
  // them across refresh). On "Przelicz", zaliczka is also added back onto expected netto so Optima
  // can deduct it once as a payroll component (same model as mandat). Keyed by driver id.
  const [mandatesAdvancesByDriverId, setMandatesAdvancesByDriverId] =
    React.useState<Record<string, { mandat: number; zaliczka: number }>>({})
  const { month } = useDate()
  const date = new Date(month)
  const dateMonth = date.getMonth() + 1
  const dateYear = date.getFullYear()

  // Zakres, ktory zajelby przycisk "Przelicz". Liczony tak samo jak na serwerze,
  // wiec "juz sie liczy" wida tu ZANIM poleci zadanie - takze wtedy, gdy uruchomil
  // je ktos inny z firmy, o czym klient wczesniej nie mial skad wiedziec.
  const calculateScopeBusy = useScopeBusy(
    companyId ? taskScopeKey.timesheet(companyId, dateYear, [dateMonth]) : null,
  )
  const wrapConfirm = useWrapConfirm()
  const removal = useRemoveTimesheets()
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)
  // Drivers in the current selection with no active contract for the viewed
  // month — same pre-calculation guard the timesheet list view shows, so the
  // user can skip them instead of the whole run failing per driver.
  const [missingContracts, setMissingContracts] = React.useState<
    { driverId: number; driverName: string }[] | null
  >(null)

  const rateQuery = useQuarterExchangeRateStatusQuery({
    companyId,
    month: dateMonth,
    year: dateYear,
  })

  const { isDirty: globalIsDirty, setDirty } = useDirtyStore(
    useShallow((s) => ({ isDirty: s.isDirty, setDirty: s.setDirty })),
  )

  const [sortBy, setSortBy] = React.useState<CellKey | null>(null)
  const [sortAsc, setSortAsc] = React.useState<boolean>(true)

  const { driversIds, searchResults, search, setSearch, ...r } = useDrivers()

  const { sideCheckedIds, sideIsActive, toggleAllSide } =
    useSideSelectionStore()

  const {
    state: toolbarState,
    toggle,
    setDomesticTripType,
    modal,
    openModal,
    closeModal,
  } = useBonusRewardsToolbar()

  const effectiveToolbarState = React.useMemo(
    () =>
      hideExtraColumns
        ? {
            ...toolbarState,
            displayBailiffDeduction: false,
            displayEquivalent: false,
            displayLeaveAdditionColumns: false,
          }
        : toolbarState,
    [hideExtraColumns, toolbarState],
  )

  const isFirstMonthRender = React.useRef(true)

  // One value for both the grid query and the calculate flow — they must send the
  // same param or the flow's final read is not reusable as grid state.
  const domesticBusinessTripDateType =
    toolbarState.domesticBusinessTripDateType ?? undefined

  const query = useGroupedPayroll({
    companyId,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    sideCheckedIds,
    showBonusConfiguration: true,
    domesticBusinessTripDateType,
  })

  // The grid is the source of truth for the actions below (bonus configs, delete
  // scope) — they stay blocked until the rows for the selection settle.
  const isRowsLoading = query.isFetching || !query.isReady

  const { isReady, drivers, total } = React.useMemo(
    () => ({
      isReady: query.isReady,
      drivers: query.data,
      total: query.data.length,
    }),
    [query.data, query.isReady],
  )

  const handleRestartState = () => {
    setIncludeBusinessTripForDriversId({})
    setSickPayByDriverId({})
    setOffPayByDriverId({})
    setBenefitsByDriverId({})
    setBonusesByDriverId({})
    setExpectedNettoAmountByDriverId({})
    setExpectedNettoBeforeTripsByDriverId({})
    manualNettoDriverIdsRef.current = new Set()
    setRewardByDriverId({})
    setTripCostsByDriverId({})
    setLeaveAdditionByDriverId({})
    // NOTE: mandat/zaliczka are BFM data (not user edits) and are NOT cleared here —
    // otherwise the post-calculate onSuccess reset would wipe them right after "Przelicz" fetched them.
    // They are cleared on month change instead (see the month effect below).
    setDirty(false)
  }

  /**
   * "Przelicz" now folds in the former "Pobierz z EasyTMS": it first pulls expected netto from EasyTMS
   * (best-effort) and merges it, then calculates. easyTmsOverrideRef carries the freshly-fetched netto
   * straight into handleSubmit, because setExpectedNettoAmountByDriverId is async and wouldn't be visible
   * to the synchronous driversInput build in the same tick.
   */
  const [isFetchingFromEasyTms, setIsFetchingFromEasyTms] =
    React.useState(false)
  const easyTmsOverrideRef = React.useRef<Record<string, number>>({})

  /**
   * Kierowcy, dla ktorych OCZEKIWANE NETTO ustawil uzytkownik: wpis w gridzie, import pliku
   * z nagrodami albo przelacznik delegacji krajowych. Pobranie z EasyTMS/BFM, ktore "Przelicz"
   * robi automatycznie na starcie, NIE nadpisuje tych wierszy.
   *
   * Bez tego edycja znikala bez zadnego sladu jeszcze przed wyslaniem zadania - BFM oddaje wartosc
   * dla kazdego kierowcy z akronimem, wiec easyTmsOverrideRef wygrywalo zawsze i grid pokazywal
   * wpisana kwote tylko do momentu klikniecia "Przelicz". Nagroda z tego ekranu i tak jest
   * zastepowana propozycja solvera, wiec oczekiwane netto to jedyna kwota sterujaca, jaka
   * uzytkownik ma - musi dzialac.
   *
   * Ref, nie stan: czytamy to w tym samym ticku, w ktorym EasyTMS oddaje wyniki (tak samo jak
   * easyTmsOverrideRef). Czyszczone razem z mapami edycji - po udanym przeliczeniu grid wraca
   * do wartosci z bazy.
   */
  const manualNettoDriverIdsRef = React.useRef<Set<string>>(new Set())

  /**
   * selected: clear/reset btn
   */
  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { dataset } = e.currentTarget
    const driverId = dataset.driverId

    if (driverId) {
      setIncludeBusinessTripForDriversId((prev) => removeKey(prev, driverId))
      setSickPayByDriverId((prev) => removeKey(prev, driverId))
      setOffPayByDriverId((prev) => removeKey(prev, driverId))
      setBenefitsByDriverId((prev) => removeKey(prev, driverId))
      setBonusesByDriverId((prev) => removeKey(prev, driverId))
      setExpectedNettoAmountByDriverId((prev) => removeKey(prev, driverId))
      setExpectedNettoBeforeTripsByDriverId((prev) => removeKey(prev, driverId))
      manualNettoDriverIdsRef.current.delete(driverId)
      setRewardByDriverId((prev) => removeKey(prev, driverId))
      setTripCostsByDriverId((prev) => removeKey(prev, driverId))
      setLeaveAdditionByDriverId((prev) => removeKey(prev, driverId))
      return
    }

    handleRestartState()
  }

  /**
   * selected ids
   */
  const selectedIds = React.useMemo(() => {
    return Object.entries(seletedRowsIds)
      .filter(([id, selected]) => selected)
      .map(([id, slected]) => id)
  }, [seletedRowsIds])

  /**
   * Kierowcy z faktyczna, niezapisana zmiana wartosci - liczy sie ROZNICA wobec wartosci
   * zapisanej (isRowEdited), a nie obecnosc wpisu w mapie edycji: MoneyInput emituje onChange
   * takze przy wyjsciu z pola, wiec samo klikniecie w komorke zostawia tam wartosc identyczna
   * z zapisana. Zaznaczenie wiersza (seletedRowsIds) to nie edycja kwoty i tez tu nie wchodzi.
   *
   * BEZ filtra po zaznaczeniu panelu bocznego - to jest zrodlo dla ostrzezenia o niezapisanych
   * zmianach, ktore musi objac takze kierowca odznaczonego juz PO edycji (jego wpisy dalej
   * siedza w mapach i wroca, gdy uzytkownik zaznaczy go z powrotem).
   */
  const editedDriverIds = React.useMemo(() => {
    const rowById = new Map(drivers.map((d) => [String(d.driver.id), d]))
    const withEntries = new Set<string>([
      ...Object.keys(includeBusinessTripForDriversId),
      ...Object.keys(sickPayByDriverId),
      ...Object.keys(benefitsByDriverId),
      ...Object.keys(bonusesByDriverId),
      ...Object.keys(offPayByDriverId),
      ...Object.keys(finalLeaveHoursByDriverId),
      ...Object.keys(expectedNettoAmountByDriverId),
      ...Object.keys(rewardByDriverId),
      ...Object.keys(leaveAdditionByDriverId),
    ])

    return Array.from(withEntries).filter((id) => {
      const row = rowById.get(id)
      // Kierowca poza gridem (odznaczony albo jeszcze niezaladowany) - nie mamy z czym
      // porownac, wiec zakladamy zmiane: lepiej ostrzec o niezapisanych zmianach niz po
      // cichu je zgubic.
      if (!row) return true
      return isRowEdited(row, {
        offPay: offPayByDriverId[id],
        sickPay: sickPayByDriverId[id],
        benefits: benefitsByDriverId[id],
        bonus: bonusesByDriverId[id],
        expectedNetto: expectedNettoAmountByDriverId[id],
        reward: rewardByDriverId[id],
        finalLeaveHours: finalLeaveHoursByDriverId[id],
        includeBusinessTrip: includeBusinessTripForDriversId[id],
        leaveAdditions: leaveAdditionByDriverId[id],
      })
    })
  }, [
    drivers,
    includeBusinessTripForDriversId,
    sickPayByDriverId,
    benefitsByDriverId,
    bonusesByDriverId,
    offPayByDriverId,
    finalLeaveHoursByDriverId,
    expectedNettoAmountByDriverId,
    rewardByDriverId,
    leaveAdditionByDriverId,
  ])

  /**
   * Zrodlo dla "Przelicz zmodyfikowanych": zmienione wiersze zawezone do zaznaczenia panelu
   * bocznego i do wierszy faktycznie zaladowanych w gridzie - bez wiersza poszlaby pusta
   * konfiguracja bez driverId, ktorej silnik nie dopasuje do kierowcy.
   */
  const modifiedDriverIds = React.useMemo(() => {
    const edited = new Set(editedDriverIds)
    const loaded = new Set(drivers.map((d) => String(d.driver.id)))
    return sideCheckedIds
      .map(String)
      .filter((id) => edited.has(id) && loaded.has(id))
  }, [editedDriverIds, drivers, sideCheckedIds])

  // Ostrzezenie o niezapisanych zmianach idzie z tego samego porownania co przycisk, ale bez
  // zawezenia do zaznaczenia - odznaczenie kierowcy nie kasuje jego edycji, wiec nie moze tez
  // kasowac ostrzezenia.
  const isDirty = editedDriverIds.length > 0

  React.useEffect(() => {
    setDirty(isDirty)
    return () => setDirty(false)
  }, [setDirty, isDirty])

  /**
   * Mutation
   */
  const onSubmitSuccessRef = React.useRef(onSubmitSuccess)
  const onSubmitErrorRef = React.useRef(onSubmitError)
  React.useEffect(() => {
    onSubmitSuccessRef.current = onSubmitSuccess
  }, [onSubmitSuccess])
  React.useEffect(() => {
    onSubmitErrorRef.current = onSubmitError
  }, [onSubmitError])

  const {
    requestConfirm: confirmForeignActivityWithoutTrip,
    modal: foreignTripConfirmModal,
  } = useForeignTripConfirm()

  const {
    requestConfirm: confirmOutOfDateExchangeRate,
    modal: outOfDateRateConfirmModal,
  } = useOutOfDateRateConfirm()

  const mutation = useSaveAndCalculate({
    confirmOutOfDateExchangeRate,
    confirmForeignActivityWithoutTrip,
    companyId,
    year: dateYear,
    month: dateMonth,
    domesticBusinessTripDateType,
    onSuccess: (freshRows) => {
      handleRestartState()
      // The flow already fetched the final rows for its mismatch check — merge them
      // instead of firing a second identical groupedTimesheet request. Refetch only
      // when that read failed (freshRows == null).
      if (freshRows && freshRows.length > 0) {
        query.mergeRows(freshRows)
      } else {
        query.refetch()
      }
      onSubmitSuccessRef.current?.()
    },
    onError: (err) => {
      // Pass 1 persists amounts before pass 2 can fail — without a refetch the grid
      // keeps pre-click values and the next "Przelicz" would send stale amounts.
      query.refetch()
      onSubmitErrorRef.current?.(err)
    },
    // driversIds: selectedIds,
  })

  React.useEffect(() => {
    if (isFirstMonthRender.current) {
      isFirstMonthRender.current = false
      return
    }
    handleRestartState()
    // Mandat/zaliczka are month-specific — drop them when the month changes (they survive recalc).
    setMandatesAdvancesByDriverId({})
  }, [month])

  // Ids of the drivers currently shown in the grid. Stable string key so the load effect below only
  // re-fires when the actual set (not just the array reference) changes.
  const displayedDriverIds = React.useMemo(
    () =>
      drivers
        .map((d) => Number(d.driver.id))
        .filter((id) => Number.isFinite(id)),
    [drivers],
  )
  const displayedDriverIdsKey = React.useMemo(
    () => [...displayedDriverIds].sort((a, b) => a - b).join(','),
    [displayedDriverIds],
  )

  // Mandat/zaliczka are display-only BFM data that is never persisted server-side, so a plain page
  // refresh (which reloads the grid from getGroupedTimesheet) would drop them. Re-pull them from
  // EasyTMS/BFM whenever the grid is (re)loaded for the current company/month so the columns survive
  // a refresh. Best-effort: if BFM is down/unconfigured the columns simply stay empty.
  React.useEffect(() => {
    if (!isReady || displayedDriverIds.length === 0) return

    let cancelled = false
    fetchMandatesAdvancesFromEasyTms({
      companyId,
      year: dateYear,
      month: dateMonth,
      driverIds: displayedDriverIds,
    })
      .then(({ data }) => {
        if (cancelled) return
        const next: Record<string, { mandat: number; zaliczka: number }> = {}
        data.forEach((row) => {
          if (row.found) {
            next[String(row.driverId)] = {
              mandat: row.mandatesSum ?? 0,
              zaliczka: row.advancesSumPln ?? 0,
            }
          }
        })
        if (Object.keys(next).length > 0) {
          setMandatesAdvancesByDriverId((prev) => ({ ...prev, ...next }))
        }
      })
      .catch(() => {
        // Best-effort: leave the mandat/zaliczka columns empty if EasyTMS is down/unconfigured.
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, companyId, dateYear, dateMonth, displayedDriverIdsKey])

  /**
   * btn submit
   */
  const handleSubmit = (targetIds?: string[]) => {
    // targetIds = "Przelicz zmodyfikowanych": tylko wiersze z edycjami. Bez niego liczymy
    // caly zaznaczony zestaw z panelu bocznego (zwykle "Przelicz").
    const submitIds = targetIds ?? sideCheckedIds.map(String)
    const driversInput = submitIds.map((id) => {
      const driverQueryData = drivers.find((d) => d.driver.id.toString() == id)
      const overrideMap = leaveAdditionByDriverId[id] || {}
      const originalLeaveEntries =
        driverQueryData?.timesheetAdditionForLeaves || []
      const mergedLeaveEntries = (() => {
        const overriddenDayTypes = new Set(Object.keys(overrideMap).map(Number))
        // Edycja użytkownika = kwota ręczna (flaga zgaszona). Wpisy nietknięte
        // wracają z flagą serwera (auto) i NIE nadpisują wyniku przeliczenia.
        const updated = originalLeaveEntries.map((entry) =>
          overriddenDayTypes.has(entry.dayType)
            ? {
                dayType: entry.dayType,
                amount: overrideMap[entry.dayType],
                isAutoGeneratedAddition: false,
              }
            : entry,
        )
        for (const dtStr of Object.keys(overrideMap)) {
          const dt = Number(dtStr)
          if (!originalLeaveEntries.some((e) => e.dayType === dt)) {
            updated.push({
              dayType: dt,
              amount: overrideMap[dt],
              isAutoGeneratedAddition: false,
            })
          }
        }
        return updated
      })()

      const queryBonusConfiguration = driverQueryData
        ? {
            bailiffDeductionAmount:
              driverQueryData.bailiffDeductionAmount ?? null,
            bonusRate:
              driverQueryData.bonus != null ? Number(driverQueryData.bonus) : 0,
            driverId: driverQueryData.driver.id,
            expectedNettoAmount:
              driverQueryData.expectedNettoAmount != null
                ? Number(driverQueryData.expectedNettoAmount)
                : 0,
            // Dla wiersza, ktory sie nie domyka, pickReward zwraca nagrode z ewidencji zamiast
            // propozycji - inaczej kazde "Przelicz" odsyla wartosc awaryjna solvera z powrotem
            // i cofniecie z przebiegu 3 nie mialoby zadnego trwalego efektu.
            rewardRate: pickReward(driverQueryData),
            timesheetAdditionForLeaves: mergedLeaveEntries,
            useValuesFromContract:
              driverQueryData.useValuesFromContract ?? false,
            benefitsGrossAmount:
              driverQueryData.benefitsGrossAmount != null
                ? Number(driverQueryData.benefitsGrossAmount)
                : 0,
            includeDomesticBusinessTrip:
              driverQueryData.includeDomesticBusinessTrip ?? false,
          }
        : {}
      const overwriteInput = {
        // easyTmsOverrideRef nie zawiera wierszy z manualNettoDriverIdsRef (odfiltrowane przy
        // zrodle w handleCalculate), wiec reczna edycja trafia tu przez expectedNettoAmountByDriverId.
        expectedNettoAmount:
          id in easyTmsOverrideRef.current
            ? easyTmsOverrideRef.current[id]
            : id in expectedNettoAmountByDriverId
              ? expectedNettoAmountByDriverId[id]
              : undefined,
        includeDomesticBusinessTrip:
          id in includeBusinessTripForDriversId
            ? includeBusinessTripForDriversId[id]
            : undefined,
        benefitsGrossAmount:
          id in benefitsByDriverId ? benefitsByDriverId[id] : undefined,
        bonusRate: id in bonusesByDriverId ? bonusesByDriverId[id] : undefined,
        leavesGrossAmount:
          id in offPayByDriverId ? offPayByDriverId[id] : undefined,
        finalLeavesGrossHours:
          id in finalLeaveHoursByDriverId
            ? finalLeaveHoursByDriverId[id]
            : undefined,
        sickLeavesGrossAmount:
          id in sickPayByDriverId ? sickPayByDriverId[id] : undefined,
        rewardRate: id in rewardByDriverId ? rewardByDriverId[id] : undefined,
      }
      return {
        id: Number(id),
        bonusConfiguration: {
          ...queryBonusConfiguration,
          expectedNettoAmount:
            overwriteInput.expectedNettoAmount !== undefined
              ? overwriteInput.expectedNettoAmount
              : queryBonusConfiguration.expectedNettoAmount,

          includeDomesticBusinessTrip:
            overwriteInput.includeDomesticBusinessTrip !== undefined
              ? overwriteInput.includeDomesticBusinessTrip
              : queryBonusConfiguration.includeDomesticBusinessTrip,

          benefitsGrossAmount:
            overwriteInput.benefitsGrossAmount !== undefined
              ? overwriteInput.benefitsGrossAmount
              : queryBonusConfiguration.benefitsGrossAmount,

          bonusRate:
            overwriteInput.bonusRate !== undefined
              ? overwriteInput.bonusRate
              : queryBonusConfiguration.bonusRate,

          ...(overwriteInput.leavesGrossAmount !== undefined && {
            leavesGrossAmount: overwriteInput.leavesGrossAmount,
          }),
          ...(overwriteInput.sickLeavesGrossAmount !== undefined && {
            sickLeavesGrossAmount: overwriteInput.sickLeavesGrossAmount,
          }),
          ...(overwriteInput.finalLeavesGrossHours !== undefined && {
            finalLeavesGrossHours: overwriteInput.finalLeavesGrossHours,
          }),

          rewardRate:
            overwriteInput.rewardRate !== undefined
              ? overwriteInput.rewardRate
              : queryBonusConfiguration.rewardRate,
        },
      }
    })

    mutation.mutate({
      drivers: driversInput,
      automaticOvertimeCompensation: toolbarState.automaticOvertimeCompensation,
      zusPaidFromAverageGross: toolbarState.zusPaidFromAverageGross,
    })

    // One-shot: the EasyTMS override only applies to the calculation that triggered the fetch.
    easyTmsOverrideRef.current = {}

    setDirty(false)
  }

  const wrappedSubmit = wrapConfirm(handleSubmit, {
    message: t('timesheet.no-exchange-rate-confirm'),
  })

  // Expose submit to the parent via ref so creator flow can trigger it from
  // its own "Next" button while the in-component action bar is hidden.
 

  const hasRealExchangeRate = React.useMemo(() => {
    if (
      rateQuery.isFetched &&
      !rateQuery.isFetching &&
      rateQuery.data &&
      rateQuery.data.month === dateMonth &&
      rateQuery.data.year === dateYear
    ) {
      return Boolean(
        rateQuery.data.isCurrentQuarterRateDeclared &&
          !rateQuery.data.isCopiedFromPreviousQuarter,
      )
    }
    return false
  }, [
    dateMonth,
    dateYear,
    rateQuery.isFetched,
    rateQuery.isFetching,
    rateQuery.data,
  ])

  /**
   * "Przelicz" action: pull expected netto (+ mandat/zaliczka) from EasyTMS (best-effort),
   * merge it, then calculate/save.
   *
   * The netto is taken from BFM exactly as returned: the top-level `expected_net_value`
   * (e.g. 8550) is already the PRE-zaliczka target — BFM's own post-zaliczka figure is the
   * nested `salary.expected_net_value` (8550 - 184.74 = 8365.26), which we do not read.
   * Optima deducts the "Zaliczka" component once on top of it, same model as Mandat (which
   * BFM does not pre-deduct either). An earlier version added advancesSumPln back onto it,
   * which walked the value straight back down to BFM's post-zaliczka figure.
   */
  const handleCalculate = async (targetIds?: string[]) => {
    // Calculating while rows are still streaming in would send empty configs for
    // unloaded drivers.
    if (isRowsLoading) return
    const calculateIds = targetIds ?? sideCheckedIds.map(String)
    if (calculateIds.length === 0) return

    // Only on the user's initial click (a confirmed "calculate for the rest"
    // pass arrives with explicit targetIds): surface drivers without an active
    // contract for this month, mirroring the timesheet list view's modal.
    if (targetIds === undefined) {
      const missing = calculateIds
        .map((id) => drivers.find((row) => String(row.driver.id) === id))
        .filter(
          (row): row is NonNullable<typeof row> =>
            Boolean(row?.hasContract) && row!.hasContract[dateMonth - 1] === 0,
        )
        .map((row) => ({
          driverId: row.driver.id,
          driverName:
            row.driver.fullName || `${row.driver.surname} ${row.driver.name}`,
        }))
      if (missing.length > 0) {
        setMissingContracts(missing)
        return
      }
    }

    const updates: Record<string, number> = {}
    const driverIds = calculateIds.map(Number)
    setIsFetchingFromEasyTms(true)

    const mandatesAdvancesById: Record<
      string,
      { mandat: number; zaliczka: number }
    > = {}

    const mandatesAdvancesPromise = fetchMandatesAdvancesFromEasyTms({
      companyId,
      year: dateYear,
      month: dateMonth,
      driverIds,
    })
      .then(({ data }) => {
        data.forEach((row) => {
          if (row.found) {
            mandatesAdvancesById[String(row.driverId)] = {
              mandat: row.mandatesSum ?? 0,
              zaliczka: row.advancesSumPln ?? 0,
            }
          }
        })
        if (Object.keys(mandatesAdvancesById).length > 0) {
          setMandatesAdvancesByDriverId((prev) => ({
            ...prev,
            ...mandatesAdvancesById,
          }))
        }
      })
      .catch(() => {
        // Best-effort: leave the mandat/zaliczka columns empty if EasyTMS is down/unconfigured.
      })

    try {
      const { data } = await fetchExpectedNettoFromEasyTms({
        companyId,
        year: dateYear,
        month: dateMonth,
        driverIds,
      })

      // Not needed for the netto (used as-is), but still awaited so the mandat/zaliczka
      // columns are populated by the time this Przelicz batch finishes.
      await mandatesAdvancesPromise

      // Only drivers EasyTMS actually returned a netto for get merged in. Drivers
      // without an akronim or with no EasyTMS data are silently left untouched —
      // a company with no EasyTMS record simply gets no overrides (and no warning toasts).
      //
      // Wiersze z recznie ustawionym oczekiwanym netto pomijamy CALKOWICIE: nie trafiaja ani do
      // gridu, ani do easyTmsOverrideRef, ktory idzie do handleSubmit. Filtrujemy w jednym
      // miejscu, przy zrodle, zeby nie dalo sie odtworzyc tego bledu przez samo dolozenie
      // kolejnego odczytu updates nizej.
      data.forEach((row) => {
        if (!row.found || row.expectedNettoAmount == null) return
        if (manualNettoDriverIdsRef.current.has(String(row.driverId))) return
        updates[String(row.driverId)] = row.expectedNettoAmount
      })

      const appliedCount = Object.keys(updates).length
      if (appliedCount > 0) {
        setExpectedNettoAmountByDriverId((prev) => ({ ...prev, ...updates }))
        toast.success({
          text: t('timesheet.easytms-fetch-success').replace(
            '{0}',
            String(appliedCount),
          ),
        })
      }
    } catch (err) {
      // Best-effort: EasyTMS down/unconfigured → keep existing values and calculate anyway.
      toast.error({ text: t('timesheet.easytms-fetch-error') })
      await mandatesAdvancesPromise
    } finally {
      setIsFetchingFromEasyTms(false)
    }

    // Carry the fresh netto into the calculation with highest priority, then run the existing flow.
    easyTmsOverrideRef.current = updates
    if (hasRealExchangeRate) handleSubmit(targetIds)
    else wrappedSubmit(targetIds)
  }

  /**
   * "Przelicz zmodyfikowanych": ten sam przeplyw co "Przelicz", ale ograniczony do kierowcow,
   * ktorych wartosci uzytkownik zmienil w gridzie (modifiedDriverIds). Pobranie z EasyTMS/BFM
   * tez idzie tylko po nich - reszta zaznaczonych wierszy nie jest ruszana ani zapisywana.
   */
  const handleCalculateModified = () => {
    if (modifiedDriverIds.length === 0) return
    handleCalculate(modifiedDriverIds)
  }

  /**
   * refresh btn
   */
  const handleRefresh = () => query.refetch()

  /**
   * delete selected btn
   */
  const handleDeleteSelected = () => {
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (isRowsLoading) return
    try {
      setShowDeleteModal(false)
      await removal.removeTimesheets({
        companyId,
        driverIds: sideCheckedIds,
        months: [dateMonth],
        year: dateYear,
      })
      toast.success({ text: t('timesheet.timesheet-deleted') })
      handleRestartState()
      query.refetch()
    } catch {
      toast.error({ text: t('timesheet.delete-error') })
    }
  }

 
  const applyIncludeBusinessTrip = (
    targetRows: PayrollDriverRow[],
    nextValue: boolean,
  ) => {
    const changed = targetRows.filter(
      (row) =>
        isDomesticTripIncluded(
          row,
          includeBusinessTripForDriversId[String(row.driver.id)],
        ) !== nextValue,
    )
    if (changed.length === 0) return

    setIncludeBusinessTripForDriversId((prev) => {
      const next = { ...prev }
      for (const row of changed) {
        const id = String(row.driver.id)
        if (nextValue === Boolean(row.includeDomesticBusinessTrip))
          delete next[id]
        else next[id] = nextValue
      }
      return next
    })

    const withTrips = changed.filter(
      (row) => row.polishTripsAmount != null && row.polishTripsAmount !== 0,
    )
    if (withTrips.length === 0) return

    const nextNetto: Record<string, number> = {}
    const capturedNetto: Record<string, number> = {}
    const releasedCaptures: string[] = []

    for (const row of withTrips) {
      const driverId = String(row.driver.id)
      const current =
        driverId in expectedNettoAmountByDriverId
          ? expectedNettoAmountByDriverId[driverId]
          : (row.expectedNettoAmount ?? 0)

      if (nextValue) {

        capturedNetto[driverId] = current
        nextNetto[driverId] = Math.max(0, current - row.polishTripsAmount)
      } else {
        nextNetto[driverId] =
          driverId in expectedNettoBeforeTripsByDriverId
            ? expectedNettoBeforeTripsByDriverId[driverId]
            : current + row.polishTripsAmount
        releasedCaptures.push(driverId)
      }
  
      manualNettoDriverIdsRef.current.add(driverId)
    }

    setExpectedNettoAmountByDriverId((prev) => ({ ...prev, ...nextNetto }))
    setExpectedNettoBeforeTripsByDriverId((prev) => {
      const next = { ...prev }
      for (const driverId of releasedCaptures) delete next[driverId]
      return { ...next, ...capturedNetto }
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked, type, dataset, name } = e.target
    const driverId = dataset.driverId
    const field = (dataset.field || name) as DriverField | undefined

    if (!driverId || !field) return

    if (type === 'checkbox') {
      const nextValue = Boolean(checked)
      switch (field) {
        case 'includeBusinessTrip': {
          const driverRow = drivers.find(
            (d) => d.driver.id.toString() === driverId,
          )
          if (driverRow) applyIncludeBusinessTrip([driverRow], nextValue)
          return
        }
        case 'isSelected':
          setSeletedRowsIds((prev) => {
            if (!nextValue) return removeKey(prev, driverId)
            return { ...prev, [driverId]: nextValue }
          })
          return
      }
    }

    if (type === 'number' || type === 'text') {
      const nextNumber = toNumberOrUndefined(value)
      switch (field) {
        case 'bonus':
          setBonusesByDriverId((prev) => ({ ...prev, [driverId]: nextNumber }))
          if (nextNumber === undefined)
            setBonusesByDriverId((prev) => removeKey(prev, driverId))
          return
        case 'benefits':
          setBenefitsByDriverId((prev) => ({ ...prev, [driverId]: nextNumber }))
          if (nextNumber === undefined)
            setBenefitsByDriverId((prev) => removeKey(prev, driverId))
          return
        case 'offPay':
          setOffPayByDriverId((prev) => ({ ...prev, [driverId]: nextNumber }))
          if (nextNumber === undefined)
            setOffPayByDriverId((prev) => removeKey(prev, driverId))
          return
        case 'finalLeaveHours':
          setFinalLeaveHoursByDriverId((prev) => ({
            ...prev,
            [driverId]: nextNumber,
          }))
          if (nextNumber === undefined)
            setFinalLeaveHoursByDriverId((prev) => removeKey(prev, driverId))
          return
        case 'sickPay':
          setSickPayByDriverId((prev) => ({ ...prev, [driverId]: nextNumber }))
          if (nextNumber === undefined)
            setSickPayByDriverId((prev) => removeKey(prev, driverId))
          return
        case 'nettoAmount': {
          const nettoRow = drivers.find(
            (d) => d.driver.id.toString() === driverId,
          )
          const currentNetto =
            driverId in expectedNettoAmountByDriverId
              ? expectedNettoAmountByDriverId[driverId]
              : (nettoRow?.expectedNettoAmount ?? 0)

    
          if (nextNumber !== undefined && nextNumber === currentNetto) {
            return
          }

          if (nextNumber === undefined) {
            // Wyczyszczone pole to rezygnacja z edycji, a nie edycja na pusto - kierowca wraca
            // pod pobranie z BFM i pod zapisana flage delegacji.
            setExpectedNettoAmountByDriverId((prev) =>
              removeKey(prev, driverId),
            )
            setExpectedNettoBeforeTripsByDriverId((prev) =>
              removeKey(prev, driverId),
            )
            setIncludeBusinessTripForDriversId((prev) =>
              removeKey(prev, driverId),
            )
            manualNettoDriverIdsRef.current.delete(driverId)
            return
          }

          setExpectedNettoAmountByDriverId((prev) => ({
            ...prev,
            [driverId]: nextNumber,
          }))
          manualNettoDriverIdsRef.current.add(driverId)

    
          setIncludeBusinessTripForDriversId((prev) =>
            nettoRow?.includeDomesticBusinessTrip
              ? { ...prev, [driverId]: false }
              : removeKey(prev, driverId),
          )
          setExpectedNettoBeforeTripsByDriverId((prev) =>
            removeKey(prev, driverId),
          )
          return
        }
        case 'reward':
          setRewardByDriverId((prev) => ({ ...prev, [driverId]: nextNumber }))
          if (nextNumber === undefined)
            setRewardByDriverId((prev) => removeKey(prev, driverId))
          return
        case 'leaveAddition': {
          const dayType = Number(dataset.dayType)
          if (!Number.isFinite(dayType)) return
          setLeaveAdditionByDriverId((prev) => {
            const driverMap = prev[driverId] ? { ...prev[driverId] } : {}
            if (nextNumber === undefined) {
              delete driverMap[dayType]
            } else {
              driverMap[dayType] = nextNumber
            }
            if (Object.keys(driverMap).length === 0) {
              const next = { ...prev }
              delete next[driverId]
              return next
            }
            return { ...prev, [driverId]: driverMap }
          })
          return
        }
        default:
          return
      }
    }
  }

  const handleHeaderClick = React.useCallback(
    (key: string) => {
      if (key.startsWith('dayType-')) return
      const name = key as CellKey
      if (sortBy === name) {
        setSortAsc((p) => !p)
      } else {
        setSortBy(name)
        setSortAsc(name === 'fullName' ? true : false)
      }
    },
    [sortBy],
  )

  const rows = React.useMemo<PayrollDriverRow[]>(() => {
    if (!isReady) return []
    let result = drivers
    const term = (search || '').trim().toLowerCase()
    if (term) {
      result = result.filter((d) =>
        (d.driver.fullName || `${d.driver.surname} ${d.driver.name}`)
          .toLowerCase()
          .includes(term),
      )
    }
    if (sortBy) {
      result = [...result].sort((a, b) =>
        compare(a, b, sortBy, sortAsc, { mandatesAdvancesByDriverId }),
      )
    }
    return result
  }, [
    isReady,
    drivers,
    total,
    sortAsc,
    sortBy,
    driversIds,
    search,
    mandatesAdvancesByDriverId,
  ])

  const [showOnlySelected, setShowOnlySelected] = React.useState(false)

  React.useEffect(() => {
    if (selectedIds.length === 0) setShowOnlySelected(false)
  }, [selectedIds.length])

  const filteredRows = React.useMemo(() => {
    let result = rows
    if (sideCheckedIds.length > 0) {
      const sideIds = new Set(sideCheckedIds.map(String))
      result = result.filter((d) => sideIds.has(String(d.driver.id)))
    } else {
      return []
    }
    if (showOnlySelected && selectedIds.length > 0) {
      const selectedSet = new Set(selectedIds.map(String))
      result = result.filter((d) => selectedSet.has(String(d.driver.id)))
    }
    return result
  }, [rows, showOnlySelected, selectedIds, sideCheckedIds])

  
  const includedTripRowsCount = React.useMemo(
    () =>
      filteredRows.filter((row) =>
        isDomesticTripIncluded(
          row,
          includeBusinessTripForDriversId[String(row.driver.id)],
        ),
      ).length,
    [filteredRows, includeBusinessTripForDriversId],
  )

  const allTripsIncluded =
    filteredRows.length > 0 && includedTripRowsCount === filteredRows.length

  const handleToggleAllBusinessTrips = (nextValue: boolean) => {
    applyIncludeBusinessTrip(filteredRows, nextValue)
  }

  const dynamicLeaveDayTypes = React.useMemo(() => {
    const set = new Set<number>()
    for (const row of filteredRows) {
      if (!row.timesheetAdditionForLeaves) continue
      for (const entry of row.timesheetAdditionForLeaves) {
        set.add(entry.dayType)
      }
    }
    return Array.from(set).sort((a, b) => a - b)
  }, [filteredRows])

  const BATCH_SIZE = 30
  const [visibleCount, setVisibleCount] = React.useState(BATCH_SIZE)
  const scrollRef = React.useRef<HTMLDivElement>(null)

  // Reset visible count when data changes
  React.useEffect(() => {
    setVisibleCount(BATCH_SIZE)
  }, [filteredRows, sortBy, sortAsc])

  const visibleRows = React.useMemo(
    () => filteredRows.slice(0, visibleCount),
    [filteredRows, visibleCount],
  )

  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    if (visibleCount >= filteredRows.length) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      setVisibleCount((prev) =>
        Math.min(prev + BATCH_SIZE, filteredRows.length),
      )
    }
  }, [visibleCount, filteredRows.length])

  const allSelected = selectedIds.length === rows.length

  const handleUnselectAll = React.useCallback(() => {
    setSeletedRowsIds({})
  }, [])

  const handleSelectAll = React.useCallback(() => {
    if (Array.isArray(drivers)) {
      const map: Record<number, true> = Object.fromEntries(
        drivers.map((row) => [row.driver.id, true]),
      )
      setSeletedRowsIds(map)
    }
  }, [allSelected, drivers])

  const uploadMutation = useUploadTimesheetFileMutation({
    companyId,
    onError: () =>
      toast.error({
        text: t('timesheet.import-file-error'),
        autoCloseMs: 3000,
      }),
    onSuccess: (importedRewards) => {
      if (importedRewards && importedRewards.length > 0) {
        const newOffPay: Record<string, number> = {}
        const newSickPay: Record<string, number> = {}
        const newExpectedNetto: Record<string, number> = {}
        const newReward: Record<string, number> = {}

        for (const item of importedRewards) {
          const driverId = item.driver.id.toString()
          if (item.leave != null) newOffPay[driverId] = item.leave
          if (item.sickLeave != null) newSickPay[driverId] = item.sickLeave
          if (item.expectedNettoAmount != null)
            newExpectedNetto[driverId] = item.expectedNettoAmount
          if (item.reward != null) newReward[driverId] = item.reward
        }

        if (Object.keys(newOffPay).length > 0)
          setOffPayByDriverId((prev) => ({ ...prev, ...newOffPay }))
        if (Object.keys(newSickPay).length > 0)
          setSickPayByDriverId((prev) => ({ ...prev, ...newSickPay }))
        if (Object.keys(newExpectedNetto).length > 0) {
          const changedIds: string[] = []
          for (const [driverId, netto] of Object.entries(newExpectedNetto)) {
            const row = drivers?.find(
              (d) => d.driver.id.toString() === driverId,
            )
            const current =
              driverId in expectedNettoAmountByDriverId
                ? expectedNettoAmountByDriverId[driverId]
                : (row?.expectedNettoAmount ?? 0)
            if (isAmountEdited(netto, current)) changedIds.push(driverId)
          }

          setExpectedNettoAmountByDriverId((prev) => ({
            ...prev,
            ...newExpectedNetto,
          }))
          if (changedIds.length > 0) {
            // Jawne false, a nie usuniecie klucza: kierowca spoza zaladowanych wierszy moze miec
            // zapadke zapisana na ewidencji.
            setIncludeBusinessTripForDriversId((prev) => ({
              ...prev,
              ...Object.fromEntries(changedIds.map((id) => [id, false])),
            }))
            setExpectedNettoBeforeTripsByDriverId((prev) => {
              const next = { ...prev }
              for (const driverId of changedIds) delete next[driverId]
              return next
            })
          }
          // Zaimportowany plik jest jawnym wyborem uzytkownika co do kwoty i ma pierwszenstwo
          // przed automatycznym pobraniem z BFM.
          for (const driverId of Object.keys(newExpectedNetto))
            manualNettoDriverIdsRef.current.add(driverId)
        }
        if (Object.keys(newReward).length > 0)
          setRewardByDriverId((prev) => ({ ...prev, ...newReward }))
      }

      toast.success({
        text: t('timesheet.import-file-success'),
        autoCloseMs: 3000,
      })
    },
  })
  const uploadInputRef = React.useRef<HTMLInputElement | null>(null)
  const handleUpload = () => {
    if (uploadMutation.isPending) return
    uploadInputRef.current?.click()
  }
  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    uploadMutation.mutate({ file })
    e.target.value = ''
  }
 const [nettoMismatchFilter, setNettoMismatchFilter] = React.useState<{
    previousIds: number[]
    appliedIds: number[]
  } | null>(null)
 const nettoMismatchRows = React.useMemo(
    () => filteredRows.filter((row) => getNettoMismatchForDisplay(row) != null),
    [filteredRows],
  )
  React.useEffect(() => {
    if (!nettoMismatchFilter) return
    const applied = nettoMismatchFilter.appliedIds
    const same =
      applied.length === sideCheckedIds.length &&
      applied.every((id) => sideCheckedIds.includes(id))
    if (!same) setNettoMismatchFilter(null)
  }, [sideCheckedIds, nettoMismatchFilter])

  const handleToggleNettoMismatchFilter = () => {
    if (nettoMismatchFilter) {
      toggleAllSide(nettoMismatchFilter.previousIds, true, sideIsActive)
      setNettoMismatchFilter(null)
      return
    }
    if (isRowsLoading || nettoMismatchRows.length === 0) return
    const appliedIds = nettoMismatchRows.map((row) => row.driver.id)
    setNettoMismatchFilter({ previousIds: sideCheckedIds, appliedIds })
    toggleAllSide(appliedIds, true, sideIsActive)
  }

   React.useEffect(() => {
    if (!submitRef) return undefined
    submitRef.current = hasRealExchangeRate ? handleSubmit : wrappedSubmit
    return () => {
      submitRef.current = null
    }
  }, [submitRef, hasRealExchangeRate, handleSubmit, wrappedSubmit])
  return (
    <PayrollActionBarProvider
      toolbarState={toolbarState}
      onToggleBailiffDeduction={() => toggle('displayBailiffDeduction')}
      onToggleEquivalent={() => toggle('displayEquivalent')}
      onToggleLeaveAdditionColumns={() => toggle('displayLeaveAdditionColumns')}
      onToggleAutomaticOvertime={() => toggle('automaticOvertimeCompensation')}
      onToggleZusPaidFromAverageGross={() => toggle('zusPaidFromAverageGross')}
      onSetDomesticTripType={setDomesticTripType}
      isDirty={isDirty}
      isRowsLoading={isRowsLoading}
      calculateBusy={calculateScopeBusy.busy}
      calculateBusyBy={calculateScopeBusy.startedBy}
      showOnlySelected={showOnlySelected}
      onToggleShowOnlySelected={() => setShowOnlySelected((p) => !p)}
      showOnlyNettoMismatch={nettoMismatchFilter != null}
      onToggleShowOnlyNettoMismatch={handleToggleNettoMismatchFilter}
      nettoMismatchCount={nettoMismatchRows.length}
      onSubmit={handleCalculate}
      onSubmitModified={handleCalculateModified}
      modifiedCount={modifiedDriverIds.length}
      onClearAll={handleRestartState}
      onDelete={handleDeleteSelected}
      onPrint={() => openModal('print')}
      printShortcuts={
        <TimesheetPrintShortcuts
          companyId={companyId}
          driverIds={sideCheckedIds.map(String)}
          dateFrom={`${dateYear}-${String(dateMonth).padStart(2, '0')}-01`}
          dateTo={formatDate(new Date(dateYear, dateMonth, 0), 'YYYY-MM-DD')}
        />
      }
      onRefresh={handleRefresh}
      onImport={handleUpload}
      onUnselect={handleUnselectAll}
    >
      <section
        className="tw-relative tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-overflow-hidden"
        data-test-id="group-payroll-view"
      >
        {!hideActionBar && <PayrollActionBar />}
        {foreignTripConfirmModal}
        {outOfDateRateConfirmModal}
        <ConfirmModal
          isOpen={missingContracts !== null}
          onClose={() => setMissingContracts(null)}
          title={t('timesheet.insufficient-data-title')}
          text={t('timesheet.contract-not-found-title')}
          content={(missingContracts ?? []).map((entry) => entry.driverName)}
          actionText={t('timesheet.calculate-for-others')}
          onConfirm={
            (missingContracts?.length ?? 0) >= sideCheckedIds.length
              ? undefined
              : () => {
                  const excluded = new Set(
                    (missingContracts ?? []).map((entry) =>
                      String(entry.driverId),
                    ),
                  )
                  setMissingContracts(null)
                  const rest = sideCheckedIds
                    .map(String)
                    .filter((id) => !excluded.has(id))
                  if (rest.length > 0) handleCalculate(rest)
                }
          }
        />
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title={t('timesheet.delete-confirmation-title')}
          text={
            sideCheckedIds.length === 1
              ? t('timesheet.delete-confirmation-single')
                  .replace(
                    '{0}',
                    rows.find((r) => r.driver.id === sideCheckedIds[0])?.driver
                      .fullName ?? '',
                  )
                  .replace(
                    '{1}',
                    new Intl.DateTimeFormat('pl-PL', { month: 'long' }).format(
                      new Date(dateYear, dateMonth - 1, 1),
                    ),
                  )
              : t('timesheet.delete-confirmation-multiple')
                  .replace('{0}', String(sideCheckedIds.length))
                  .replace(
                    '{1}',
                    new Intl.DateTimeFormat('pl-PL', { month: 'long' }).format(
                      new Date(dateYear, dateMonth - 1, 1),
                    ),
                  )
          }
          content={
            sideCheckedIds.length > 1
              ? sideCheckedIds.map(
                  (id) =>
                    rows.find((r) => r.driver.id === id)?.driver.fullName ??
                    String(id),
                )
              : undefined
          }
          actionText={t('common.delete')}
          onConfirm={handleConfirmDelete}
        />

        {/* Print modal */}
        <PrintActionContainer
          companyId={companyId}
          dateFrom={`${dateYear}-${String(dateMonth).padStart(2, '0')}-01`}

          dateTo={formatDate(new Date(dateYear, dateMonth, 0), 'YYYY-MM-DD')}
          driverIds={sideCheckedIds.map(String)}
          availableReports={GROUP_PAYROLL_AVAILABLE_REPORTS}
          isOpen={modal === 'print'}
          setOpen={(open) => {
            if (!open) closeModal()
          }}
        />

        {/* Hidden uploa */}
        <input
          ref={uploadInputRef}
          type="file"
          className="tw-hidden"
          onChange={onFileChange}
        />

        {/* error */}
        {query.isError && <PayrollErrorScreen error={query.error || ''} />}

        {/* pending */}
        {query.isFetching && <Loader fill position="absolute" />}

        {mutation.isPending ||
        removal.isPending ||
        uploadMutation.isPending ||
        isFetchingFromEasyTms ? (
          <Loader
            className="tw-z-50 !tw-flex-col !tw-justify-start tw-p-10"
            fill
            position="absolute"
            text={removal.isPending ? removal.statusText : mutation.statusText}
            shadow
            spinnerClassName="tw-mt-[20vh]"
          />
        ) : null}

        {/* empty */}
        {isReady && !query.isError && !drivers ? <PayrollEmptyScreen /> : null}

        {/* results */}
        {filteredRows.length ? (
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="tw-min-h-0 tw-min-w-0 tw-flex-1 tw-overflow-auto"
          >
            <PayrollResultsTableWrapper
              displayBailiffDeductionAmount={
                effectiveToolbarState.displayBailiffDeduction
              }
              displayEquivalent={effectiveToolbarState.displayEquivalent}
              sortBy={sortBy}
              sortAsc={sortAsc}
              onHeaderClick={handleHeaderClick}
              includeAllBusinessTrips={allTripsIncluded}
              includeSomeBusinessTrips={includedTripRowsCount > 0}
              onToggleAllBusinessTrips={handleToggleAllBusinessTrips}
              dynamicLeaveDayTypes={
                effectiveToolbarState.displayLeaveAdditionColumns
                  ? dynamicLeaveDayTypes
                  : []
              }
            >
              {visibleRows.map((d, i) => {
                const driverId = d.driver.id
                return (
                  <PayrollResultsTableRow
                    row={d}
                    key={`${d.driver.id}-${seletedRowsIds[driverId]}-${includeBusinessTripForDriversId[driverId]}-${expectedNettoAmountByDriverId[driverId]}-${offPayByDriverId[driverId]}${finalLeaveHoursByDriverId[driverId]}-${sickPayByDriverId[driverId]}-${benefitsByDriverId[driverId]}-${bonusesByDriverId[driverId]}-${rewardByDriverId[driverId]}-${tripCostsByDriverId[driverId]}-${mandatesAdvancesByDriverId[driverId]?.mandat}-${mandatesAdvancesByDriverId[driverId]?.zaliczka}`}
                    handleChange={handleChange}
                    displayBailiffDeductionAmount={
                      effectiveToolbarState.displayBailiffDeduction
                    }
                    displayEquivalent={effectiveToolbarState.displayEquivalent}
                    expectedNetto={expectedNettoAmountByDriverId[driverId]}
                    expectedNettoBeforeTrips={
                      expectedNettoBeforeTripsByDriverId[driverId]
                    }
                    includeBusinessTrip={
                      includeBusinessTripForDriversId[driverId]
                    }
                    offPay={offPayByDriverId[driverId]}
                    finalLeaveHours={finalLeaveHoursByDriverId[driverId]}
                    sickPay={sickPayByDriverId[driverId]}
                    benefits={benefitsByDriverId[driverId]}
                    bonus={bonusesByDriverId[driverId]}
                    reward={rewardByDriverId[driverId]}
                    tripCosts={tripCostsByDriverId[driverId]}
                    mandat={mandatesAdvancesByDriverId[driverId]?.mandat}
                    zaliczka={mandatesAdvancesByDriverId[driverId]?.zaliczka}
                    dynamicLeaveDayTypes={
                      effectiveToolbarState.displayLeaveAdditionColumns
                        ? dynamicLeaveDayTypes
                        : []
                    }
                    leaveAdditions={leaveAdditionByDriverId[driverId]}
                  />
                )
              })}
            </PayrollResultsTableWrapper>
          </div>
        ) : null}
      </section>
    </PayrollActionBarProvider>
  )
}
